import React, { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import { usePaginatedService } from '@aph/common-react';
import queryString from 'query-string';
import dayjs from 'dayjs';
import { defaultPageSize } from 'global.constants';
import { ILibrarySearchFormData } from 'types/form.types';
import { setupDateForRequest } from 'helpers/form.helpers';
import { parseArrayUrlParameter, updateUrlSearchParameters } from 'global.helpers';
import {
  IUseSearchService,
  ILectureSearchRequest,
  ILibrarySearchResponse
} from 'services';

import { LectureVideoView } from 'components';
import { LectureSearchInner } from 'search-interfaces';

import './LectureSearch.scss';

/**
 * BaseLectureSearchRequest
 *
 * @description Base search request for searching lectures
 */
const baseLectureSearchRequest: ILectureSearchRequest = {
  topics: [],
  currentPage: 1,
  pageSize: defaultPageSize
};

/**
 * InitialFormValues
 *
 * @description Initial form values for publication search filter form
 */
const initialFormValues: ILibrarySearchFormData = {
  searchTerms: '',
  publicationType: null,
  topics: [],
  dateFrom: null,
  dateTo: null
};

/**
 * ILectureSearch
 *
 * @description Component properties for the Lecture search component
 */
interface ILectureSearch {
  searchService: IUseSearchService;
}

/**
 * GetInitialVideoIdFromQueryParam
 *
 * @description Helper method to retrieve the initial video ID from the query param if there is one
 * @returns {string | null} Selected video ID if there is one
 */
const getInitialVideoIdFromQueryParam = (): string | null => {
  const serialisedQuery = queryString.parse(window.location.search);

  if (!serialisedQuery) {
    return null;
  }

  const selectedVideoId = serialisedQuery['selectedVideo'];

  if (!selectedVideoId) {
    return null;
  }

  return typeof (selectedVideoId) === 'string'
    ? selectedVideoId
    : null;
};

/**
 * LectureSearch
 *
 * @description Seach and display lectures from sitecore
 */
const LectureSearch: React.FC<ILectureSearch> = ({ searchService }) => {
  const formRef = useRef<any>();

  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(getInitialVideoIdFromQueryParam());

  const {
    loading,
    currentPage,
    rowCount,
    pageSize,
    results,
    request: currentSearchRequest,
    refreshResults: refreshSearchResults,
    updatePagination,
  } = usePaginatedService<ILectureSearchRequest, ILibrarySearchResponse>(searchService.searchLectures, baseLectureSearchRequest, false);

  // Setup the initial search results we need to first pass the URL params
  useEffect(() => {
    const parsedQueryParameters = queryString.parse(window.location.search);

    // To deal with arrays not serialising correctly with a single value we need to parse it using our custom helper
    // This means parameter=value will become ["value"] instead of "value"
    const topicsParsed = parseArrayUrlParameter(parsedQueryParameters, 'topics');
    const dateFrom = typeof parsedQueryParameters['dateFrom'] === 'string' ? dayjs(parsedQueryParameters['dateFrom']) : null;
    const dateTo = typeof parsedQueryParameters['dateTo'] === 'string' ? dayjs(parsedQueryParameters['dateTo']) : null;

    const initialLectureSearchRequest: ILectureSearchRequest = {
      ...baseLectureSearchRequest,
      ...parsedQueryParameters,
      topics: topicsParsed
    };

    refreshSearchResults(initialLectureSearchRequest);

    if (formRef.current) {
      formRef.current.setValues({
        ...initialFormValues,
        ...parsedQueryParameters,
        topics: topicsParsed,
        dateFrom: dateFrom,
        dateTo: dateTo
      });
    }
  }, []);

  /**
   * HandleSubmitSearch
   *
   * @description Form submission handler
   * @param {ILibrarySearchFormData} values Form values
   * @returns {void}
   */
  const handleSubmitSearch = (values: ILibrarySearchFormData): void => {
    const updatedSearchRequest: ILectureSearchRequest = {
      currentPage: 1,
      pageSize: defaultPageSize,
      topics: values.topics
    };

    if (values.searchTerms) {
      updatedSearchRequest.searchTerms = values.searchTerms;
    }

    updatedSearchRequest.dateFrom = setupDateForRequest(values?.dateFrom);
    updatedSearchRequest.dateTo = setupDateForRequest(values?.dateTo);

    refreshSearchResults(updatedSearchRequest);
  };

  return (
    <div className='lecture-search'>
      <Formik<ILibrarySearchFormData>
        initialValues={{
          searchTerms: '',
          publicationType: '',
          topics: [],
          dateFrom: null,
          dateTo: null
        }}
        // We don't need to run the submit because we are actually refreshing the results on change of each field
        onSubmit={handleSubmitSearch}
        onReset={() => {
          window.history.replaceState(null, '', '?');
          refreshSearchResults(baseLectureSearchRequest);
        }}
        validateOnChange={false}
        validateOnBlur={false}
        innerRef={formRef}
      >
        {
          selectedVideoId
            ?
            <LectureVideoView
              videoId={selectedVideoId}
              searchService={searchService}
              onBackClickHandler={() => {
                setSelectedVideoId(null);
                updateUrlSearchParameters('selectedVideo', '');
              }}
            />
            :
            <LectureSearchInner
              searchService={searchService}
              searchResults={{
                loading,
                results: results,
                currentPage,
                pageSize,
                rowCount
              }}
              setSelectedVideo={setSelectedVideoId}
              currentSearchRequest={currentSearchRequest}
              paginationHandler={updatePagination}
              filterSearchResults={refreshSearchResults}
            />
        }
      </Formik>
    </div>
  );
};

export default LectureSearch;
